/**
 * Grant Traceability Component
 *
 * Shows which grants provided each permission with links to grant editor
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  TextField,
  InputAdornment,
  Badge,
  Avatar,
  Grid
} from '@mui/material';
import {
  ExpandMore,
  Edit,
  Person,
  Groups,
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Search,
  AccessTime,
  PriorityHigh
} from '@mui/icons-material';

export default function GrantTraceability({ grants = [], permissions = {}, onGrantEdit }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGrant, setExpandedGrant] = useState(null);

  // Ensure grants is always an array
  const grantsArray = useMemo(() => {
    return Array.isArray(grants) ? grants : [];
  }, [grants]);

  // Filter grants based on search
  const filteredGrants = useMemo(() => {
    if (!searchTerm) return grantsArray;

    return grantsArray.filter(grant =>
      grant.grantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grant.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grant.resources?.some(r => r.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [grantsArray, searchTerm]);

  // Group grants by type and status
  const groupedGrants = useMemo(() => {
    const groups = {
      userGrants: [],
      roleGrants: [],
      activeGrants: [],
      temporalGrants: [],
      inactiveGrants: []
    };

    filteredGrants.forEach(grant => {
      if (grant.targetType === 'USER') {
        groups.userGrants.push(grant);
      } else {
        groups.roleGrants.push(grant);
      }

      if (grant.status === 'ACTIVE') {
        groups.activeGrants.push(grant);
      } else {
        groups.inactiveGrants.push(grant);
      }

      if (grant.effectiveFrom || grant.effectiveTo) {
        groups.temporalGrants.push(grant);
      }
    });

    return groups;
  }, [filteredGrants]);

  const getGrantIcon = (grant) => {
    if (grant.targetType === 'USER') {
      return <Person fontSize="small" />;
    }
    return <Groups fontSize="small" />;
  };

  const getGrantStatusColor = (grant) => {
    if (grant.status !== 'ACTIVE') return 'default';

    const now = new Date().toISOString();
    if (grant.effectiveTo && grant.effectiveTo < now) return 'error';
    if (grant.effectiveFrom && grant.effectiveFrom > now) return 'warning';

    return 'success';
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'error';
    if (priority >= 5) return 'warning';
    return 'primary';
  };

  const countPermissionsFromGrant = (grant) => {
    if (!grant.actionRules) return { allow: 0, deny: 0 };

    let allow = 0;
    let deny = 0;

    Object.values(grant.actionRules).forEach(resourceRules => {
      Object.values(resourceRules).forEach(permission => {
        if (permission === 'ALLOW') allow++;
        else if (permission === 'DENY') deny++;
      });
    });

    return { allow, deny };
  };

  const renderGrantDetails = (grant) => {
    const permissionCounts = countPermissionsFromGrant(grant);

    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            {getGrantIcon(grant)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2">
              {grant.grantName || `Grant ${grant.id?.slice(0, 8)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {grant.id}
            </Typography>
          </Box>
          <Tooltip title="Edit Grant">
            <IconButton
              size="small"
              onClick={() => onGrantEdit && onGrantEdit(grant.id)}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Grant Properties */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {getGrantIcon(grant)}
              <Typography variant="body2">{grant.targetType}</Typography>
            </Stack>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={grant.status}
              size="small"
              color={getGrantStatusColor(grant)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Priority
            </Typography>
            <Chip
              label={grant.priority || 1}
              size="small"
              color={getPriorityColor(grant.priority || 1)}
              variant="outlined"
              icon={grant.priority >= 8 ? <PriorityHigh /> : null}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Permissions
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {permissionCounts.allow > 0 && (
                <Chip
                  label={`${permissionCounts.allow} Allow`}
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircle />}
                />
              )}
              {permissionCounts.deny > 0 && (
                <Chip
                  label={`${permissionCounts.deny} Deny`}
                  size="small"
                  color="error"
                  variant="outlined"
                  icon={<Cancel />}
                />
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Target Details */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Target
          </Typography>
          {grant.targetType === 'USER' ? (
            <Typography variant="body2">
              User ID: {grant.targetUserId}
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary">Group:</Typography>
                <Chip label={grant.targetGroup || 'ALL'} size="small" variant="outlined" />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary">Role:</Typography>
                <Chip label={grant.targetGroupRole || 'ALL'} size="small" variant="outlined" />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2" color="text.secondary">Org Level:</Typography>
                <Chip label={grant.targetOrgLevel || 'ALL'} size="small" variant="outlined" />
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Resources */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Resources ({grant.resources?.length || 0})
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {grant.resources?.map(resource => (
              <Chip
                key={resource}
                label={resource}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>

        {/* Temporal Constraints */}
        {(grant.effectiveFrom || grant.effectiveTo) && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccessTime />
              <Typography variant="subtitle2">Temporal Constraints</Typography>
            </Stack>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {grant.effectiveFrom && (
                <Typography variant="body2">
                  From: {new Date(grant.effectiveFrom).toLocaleString()}
                </Typography>
              )}
              {grant.effectiveTo && (
                <Typography variant="body2">
                  To: {new Date(grant.effectiveTo).toLocaleString()}
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Description */}
        {grant.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {grant.description}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Search and Summary */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search grants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />

          <Stack direction="row" spacing={1}>
            <Chip
              icon={<Person />}
              label={`User: ${groupedGrants.userGrants.length}`}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<Groups />}
              label={`Role: ${groupedGrants.roleGrants.length}`}
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<AccessTime />}
              label={`Temporal: ${groupedGrants.temporalGrants.length}`}
              size="small"
              color={groupedGrants.temporalGrants.length > 0 ? 'warning' : 'default'}
              variant="outlined"
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Grants List */}
      {filteredGrants.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No grants match your search' : 'No grants found for this user'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredGrants.map((grant, index) => {
            const isExpanded = expandedGrant === grant.id;
            const permissionCounts = countPermissionsFromGrant(grant);

            return (
              <Accordion
                key={grant.id}
                expanded={isExpanded}
                onChange={() => setExpandedGrant(isExpanded ? null : grant.id)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ width: '100%' }}
                  >
                    <Badge
                      badgeContent={grant.priority || 1}
                      color={getPriorityColor(grant.priority || 1)}
                    >
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {getGrantIcon(grant)}
                      </Avatar>
                    </Badge>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">
                        {grant.grantName || `Grant ${index + 1}`}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={grant.targetType}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={grant.status}
                          size="small"
                          color={getGrantStatusColor(grant)}
                          variant="outlined"
                        />
                        {permissionCounts.allow > 0 && (
                          <Chip
                            label={`${permissionCounts.allow}✓`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {permissionCounts.deny > 0 && (
                          <Chip
                            label={`${permissionCounts.deny}✗`}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      {grant.resources?.slice(0, 3).map(resource => (
                        <Chip
                          key={resource}
                          label={resource}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {grant.resources?.length > 3 && (
                        <Chip
                          label={`+${grant.resources.length - 3}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  {renderGrantDetails(grant)}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Info Box */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Info />
          <Typography variant="body2">
            Grants are applied in priority order. Higher priority grants override lower priority ones.
            DENY permissions always take precedence over ALLOW.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}